import React from 'react';
import { trainingModeStyles } from '../../common/training-mode/training-mode-styles';
import { useSelector } from 'react-redux';
import { AppState } from '../../common/redux/configure-store';
import { Grid, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { transactionSearchModalStyles } from '../transaction-based-return-styles';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  DateRange,
  ReturnPolicyResponse,
} from '../interfaces/get-effective-return-policy-response';
import { ISearchParameterInputProps } from './search-parameter-input';
import { POSModeState } from '../../common/interfaces/states/POSModeState';
import { ITransactionSearchState } from 'src/common/interfaces/states/ITransactionSearchState';
import SearchParameterCalendar from './search-parameter-calendar';
import SearchParameterInput from './search-parameter-input';
import SearchByParameterScreenActionButtons from './parameter-search-screen-action-buttons';

export interface ParameterSearchProps {
  dateRange: DateRange;
  setSearchParam: (searchParam: Partial<ITransactionSearchState>) => void;
  storeNumber?: string;
  posNumber?: string;
  transactionNumber?: string;
  effectivePolicy?: ReturnPolicyResponse;
  onSearch: () => Promise<void>;
  onCancel: () => void;
  onBack: () => void;
  testIdPrefix: string;
  hidePolicyMaxButton: boolean;
}

const ParameterSearchScreen = (props: ParameterSearchProps): JSX.Element => {
  const {
    dateRange,
    setSearchParam,
    storeNumber,
    posNumber,
    transactionNumber,
    effectivePolicy,
    testIdPrefix,
    hidePolicyMaxButton,
  } = props;
  const classes = transactionSearchModalStyles();
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const leftHanded = useSelector((state: AppState): boolean => state.cashierPreferences.leftHanded);
  const posModeState = useSelector((state: AppState): POSModeState => state.posMode);

  const handleSubmitForm = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    props.onSearch();
  };

  const searchParameterInputsArr: ISearchParameterInputProps[] = [
    {
      onChange: e => setSearchParam({ storeNumber: e.target.value }),
      dataTestIdPrefix: `${testIdPrefix}-store-number`,
      name: 'storeNumber',
      value: storeNumber || '',
      label: t('Store Number'),
    },
    {
      onChange: e => setSearchParam({ posNumber: e.target.value }),
      dataTestIdPrefix: `${testIdPrefix}-pos-number`,
      name: 'posNumber',
      value: posNumber || '',
      label: t('POS Number'),
    },
    {
      onChange: e => setSearchParam({ transactionNumber: e.target.value }),
      dataTestIdPrefix: `${testIdPrefix}-transaction-number`,
      name: 'transactionNumber',
      value: transactionNumber || '',
      label: t('Transaction Number'),
    },
  ];

  return (
    <Grid
      item
      container
      className={`fullHeight flexFull`}
      direction={leftHanded ? 'row-reverse' : 'row'}
      data-testid='transaction-search-screen-container'
    >
      <Grid
        item
        className={`${
          posModeState.trainingMode
            ? trainingModeClasses.trainingModeBackground
            : classes.background
        } fullWidth fullHeight`}
      >
        <form onSubmit={handleSubmitForm} noValidate data-testid='transaction-search-form'>
          <Grid
            container
            direction='column'
            className={`fullHeight ${classes.returnItemContainer} ${classes.outerFlexDesgin}`}
          >
            <Grid container direction='column' justifyContent='center' className={`fullWidth`}>
              <Grid item className={classes.fitHeight}>
                <Grid container direction='column' className={`fullWidth`}>
                  <Typography
                    variant='h2'
                    className={`${classes.returnItemTitleHeader} ${classes.codeContainerSubHeaderLabel}`}
                    data-testid='transaction-search-title'
                  >
                    {t('Transaction Search')}
                  </Typography>
                  <Grid item xs={1}>
                    <Typography className={`${classes.returnItemTitleBar}`} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <span
              className={classes.returnItemTitleSubheader}
              data-testid='transaction-search-helper-text'
            >
              {t('Enter the details below and tap "search" to submit.')}
            </span>
            <Grid item container>
              <Grid item xs={1} />
              <Grid container item xs={7}>
                <SearchParameterCalendar
                  setDateRange={input => setSearchParam({ dateRange: input })}
                  effectivePolicy={effectivePolicy}
                  dateRange={dateRange}
                  hidePolicyMaxButton={hidePolicyMaxButton}
                  testIdPrefix={testIdPrefix}
                />
                {searchParameterInputsArr.map((tbrObject, i) => (
                  <SearchParameterInput
                    key={tbrObject.name}
                    onChange={tbrObject.onChange}
                    dataTestIdPrefix={tbrObject.dataTestIdPrefix}
                    name={tbrObject.name}
                    value={tbrObject.value}
                    label={tbrObject.label}
                    index={i}
                  />
                ))}
              </Grid>
              <Grid xs={4} item container className={classes.labelHeadTop}>
                <SearchByParameterScreenActionButtons
                  onSearch={props.onSearch}
                  onCancel={props.onCancel}
                  onBack={props.onBack}
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default ParameterSearchScreen;
